import React, { useEffect, useState } from 'react';
import {
    Card,
    CardContent,
    Grid,
    Typography,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormControl,
    FormLabel,
    Box,
} from '@mui/material';
import { TextInput } from 'components/Input';
import { currencyMask } from 'helpers/masks';
import { formatDollar, unformatNumberString } from 'helpers/formatters';
import { useSelector } from 'react-redux';
import {
    getSovLocations,
    getSubmissionDetails,
    getUwBRPropertyValues,
    getUwBRReview,
    getUwBRSubscribers,
} from 'application/redux/selectors';
import * as Actions from 'application/redux/actions';
import { useActions } from 'hooks/useActions';
import { Spacing } from 'components/Layout';
import {
    applyPricingTableToBrLocationReview,
    calculateBrPricingTable,
    getReviewLumenParticipationPercent,
    getBrReviewTriaPremiumFromLocations,
    getBrTotalProgramPremiumFromLocationsWithParticipation,
    getBrPremiumSubtotal,
    getParticipationAdjustedTotalPremium,
} from 'helpers/brCalculationHelper';
import { Location } from 'types/location.type';
import { calculateTriaPremiumFromTotalPremium } from 'helpers/calculations';
import { useFormik } from 'formik';
import { validateBrUnderwriterReview, getCombinedSubscriberCapacity } from './Helpers/validations';
import { COLORS } from '../../../../../../constants';
import BuildersRiskSubjectivities from './BuildersRiskSubjectivities';
import BuildersRiskSubscription from './BuildersRiskSubscription';
import { getProductFilteredLocations } from '../../../../../../helpers/submissions';
import { updateSOVLocationUWNotes, uwReviewSaveAllBRProperties } from 'application/redux/actions';

interface TopLevelReviewCardProps {
    readOnly: boolean;
}

const disabledLabelStyle = {
    '.MuiFormControlLabel-label.Mui-disabled': {
        color: COLORS.SECONDARY_LIGHT,
    },
};

const MY_PRODUCT_LINE_TYPE = 'BUILDERS_RISK';

export const TopLevelReviewCard: React.FC<TopLevelReviewCardProps> = ({ readOnly }) => {
    // Global State
    const submission = useSelector(getSubmissionDetails);
    const uwBrReview = useSelector(getUwBRReview);
    const brPropertyValues = useSelector(getUwBRPropertyValues);
    const locations = useSelector(getSovLocations);
    const subscriptionCarriers = useSelector(getUwBRSubscribers);
    const { uwReviewSaveBRReview } = useActions(Actions);

    // Sum of capacities for participating subscribers
    const combinedSubscriberCapacity = React.useMemo(
        () => getCombinedSubscriberCapacity(subscriptionCarriers ?? undefined),
        [subscriptionCarriers]
    );

    const [totalPremium, setTotalPremium] = useState<number | null>(uwBrReview.premium ?? null);
    const [triaPremium, setTriaPremium] = useState<number | null>(
        uwBrReview.premium != null
            ? calculateTriaPremiumFromTotalPremium(uwBrReview.premium, uwBrReview.triaPercentage || 0)
            : null
    );
    const [programPremium, setProgramPremium] = useState<number>(0);
    const [productFilteredLocations, setProductFilteredLocations] = useState<Location[]>([]);

    useEffect(() => {
        setProductFilteredLocations(getProductFilteredLocations(MY_PRODUCT_LINE_TYPE, locations, submission));
    }, [locations]);

    useEffect(() => {
        setTotalPremium(uwBrReview.premium ?? null);
    }, [uwBrReview]);

    // Formik setup
    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            isLeader: !!uwBrReview.isLeader,
            policyLimitOfLiability: uwBrReview.policyLimitOfLiability || '',
            lumenLimitOfLiability: uwBrReview.lumenLimitOfLiability || '',
        },
        // tslint:disable-next-line:no-empty
        onSubmit: () => {},
        validate: (values) => validateBrUnderwriterReview(values, combinedSubscriberCapacity),
        validateOnMount: true,
    });

    // Re-run validation when participating subscribers change so the capacity check stays current
    useEffect(() => {
        formik.validateForm();
    }, [combinedSubscriberCapacity]);

    const lumenParticipationStr = getReviewLumenParticipationPercent(
        unformatNumberString(formik.values.policyLimitOfLiability),
        unformatNumberString(formik.values.lumenLimitOfLiability)
    );
    const lumenParticipationNum = Number(lumenParticipationStr);

    // Sync premium and TRIA premium when location reviews change
    useEffect(() => {
        const applied = brPropertyValues.map((propertyValue) => {
            const pfLocation = productFilteredLocations.find((loc) => loc.id === propertyValue.locationId)!;
            return pfLocation
                ? applyPricingTableToBrLocationReview(
                      propertyValue,
                      calculateBrPricingTable(pfLocation, propertyValue, submission)
                  )
                : propertyValue;
        });

        const depositPremium =
            formik.values.policyLimitOfLiability && formik.values.lumenLimitOfLiability
                ? getBrTotalProgramPremiumFromLocationsWithParticipation(applied, lumenParticipationNum)
                : null;
        setTotalPremium(depositPremium);

        const tria =
            formik.values.policyLimitOfLiability && formik.values.lumenLimitOfLiability
                ? getBrReviewTriaPremiumFromLocations(applied, lumenParticipationNum)
                : null;
        setTriaPremium(tria);

        // Calculate program premium (sum of program premiums from all locations, no TRIA)
        const totalProgramPremium = applied.reduce((sum, lr) => sum + (lr.programPremium ?? 0), 0);
        setProgramPremium(totalProgramPremium);
    }, [brPropertyValues, formik.values]);

    // Handle change for all inputs
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type } = event.target;
        let newValue: any = value;
        if (type === 'radio') {
            newValue = JSON.parse(value);
        }

        if (name === 'policyLimitOfLiability' || name === 'lumenLimitOfLiability') {
            newValue = unformatNumberString(value);
        }

        formik.handleChange(event);

        uwReviewSaveBRReview({ ...uwBrReview, [name]: newValue });
    };

    useEffect(() => {
        // Update location reviews based on new lumen participation
        if (formik.values.lumenLimitOfLiability && formik.values.policyLimitOfLiability) {
            const updatedLocationReviews = brPropertyValues.map((propertyValue) => {
                propertyValue.totalPremium =
                    getParticipationAdjustedTotalPremium(
                        propertyValue,
                        Number(
                            getReviewLumenParticipationPercent(
                                Number(formik.values.policyLimitOfLiability),
                                Number(formik.values.lumenLimitOfLiability)
                            )
                        )
                    ) ?? undefined;
                return propertyValue;
            });
            uwReviewSaveAllBRProperties(updatedLocationReviews);
        }
    }, [formik.values.policyLimitOfLiability, formik.values.lumenLimitOfLiability]);

    const lumenParticipation = getReviewLumenParticipationPercent(
        unformatNumberString(formik.values.policyLimitOfLiability),
        unformatNumberString(formik.values.lumenLimitOfLiability)
    );

    return (
        <Card sx={{ paddingBottom: 1 }}>
            <CardContent style={{ paddingBottom: 0 }}>
                <form>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Grid container spacing={8}>
                                <Grid item xs={12}>
                                    <Grid
                                        container
                                        direction="row"
                                        justifyContent="start"
                                        alignItems="center"
                                        spacing={6}
                                    >
                                        <Grid item>
                                            <FormControl sx={{ flexDirection: 'column' }}>
                                                <FormLabel>Lead/Follow</FormLabel>
                                                <RadioGroup
                                                    row
                                                    name="isLeader"
                                                    value={formik.values.isLeader}
                                                    onChange={handleChange}
                                                >
                                                    <FormControlLabel
                                                        value={true}
                                                        control={<Radio color="secondary" disabled={readOnly} />}
                                                        label="Lead"
                                                        sx={disabledLabelStyle}
                                                    />
                                                    <FormControlLabel
                                                        value={false}
                                                        control={<Radio color="secondary" disabled={readOnly} />}
                                                        label="Follow"
                                                        sx={disabledLabelStyle}
                                                    />
                                                </RadioGroup>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                    <Grid
                                        container
                                        direction="row"
                                        justifyContent="start"
                                        alignItems="center"
                                        spacing={8}
                                    >
                                        <Grid item>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12}>
                                                    <FormControl>
                                                        <FormLabel>Policy Limit of Liability</FormLabel>
                                                        <TextInput
                                                            variant="underlined"
                                                            name="policyLimitOfLiability"
                                                            value={formik.values.policyLimitOfLiability}
                                                            mask={currencyMask}
                                                            onChange={handleChange}
                                                            error={!!formik.errors.policyLimitOfLiability}
                                                            onBlur={formik.handleBlur}
                                                            readOnly={readOnly}
                                                        />
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <FormControl>
                                                        <FormLabel>Lumen Limit of Liability</FormLabel>
                                                        <TextInput
                                                            variant="underlined"
                                                            name="lumenLimitOfLiability"
                                                            value={formik.values.lumenLimitOfLiability}
                                                            mask={currencyMask}
                                                            errorMessage={
                                                                formik.errors.lumenLimitOfLiability &&
                                                                !!formik.values.lumenLimitOfLiability
                                                                    ? String(formik.errors.lumenLimitOfLiability)
                                                                    : !!formik.values.lumenLimitOfLiability &&
                                                                      unformatNumberString(
                                                                          formik.values.lumenLimitOfLiability
                                                                      ) >
                                                                          unformatNumberString(
                                                                              formik.values.policyLimitOfLiability
                                                                          )
                                                                    ? 'Lumen limit must be <= policy limit'
                                                                    : ''
                                                            }
                                                            onChange={handleChange}
                                                            error={!!formik.errors.lumenLimitOfLiability}
                                                            onBlur={formik.handleBlur}
                                                            readOnly={readOnly}
                                                        />
                                                    </FormControl>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid alignSelf="end">
                                            <Grid item xs={12}>
                                                <FormControl>
                                                    <FormLabel>Lumen Participation %</FormLabel>
                                                    <Typography variant="body1" color="text.primary" marginTop={0.5}>
                                                        {lumenParticipation}%
                                                    </Typography>
                                                    <Box sx={{ m: 2.1 }}></Box>
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                    <BuildersRiskSubscription readOnly={readOnly} />
                                </Grid>
                                <Grid item xs={12}>
                                    <Grid
                                        container
                                        direction="row"
                                        justifyContent="start"
                                        alignItems="center"
                                        spacing={8}
                                    >
                                        <Grid item>
                                            <FormControl>
                                                <FormLabel>Deposit Premium</FormLabel>
                                                <Typography variant="body1" color="text.primary" marginTop={0.5}>
                                                    {totalPremium == null || totalPremium === 0
                                                        ? '$-'
                                                        : formatDollar(totalPremium, 0)}
                                                </Typography>
                                            </FormControl>
                                        </Grid>
                                        <Grid item>
                                            <FormControl>
                                                <FormLabel>Tria Premium</FormLabel>
                                                <Typography variant="body1" color="text.primary" marginTop={0.5}>
                                                    {triaPremium == null || triaPremium === 0
                                                        ? '$-'
                                                        : formatDollar(triaPremium, 0)}
                                                </Typography>
                                            </FormControl>
                                        </Grid>
                                        <Grid item>
                                            <FormControl>
                                                <FormLabel>Program Premium</FormLabel>
                                                <Typography variant="body1" color="text.primary" marginTop={0.5}>
                                                    {programPremium === 0 ? '$-' : formatDollar(programPremium, 0)}
                                                </Typography>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                    <BuildersRiskSubjectivities readOnly={readOnly} />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Spacing height="2rem" />
                </form>
            </CardContent>
        </Card>
    );
};
